/**
 * @paradigm/terminal-core public surface.
 *
 * The hosts (Paradex `ui/paradex/`, Paradigm `ui/desktop/`) import factories
 * and types from this entry. Each host wraps the factories with its own
 * key-shape and namespace config; see `ui/terminal/MERGE_NOTES.md`.
 */

export const TERMINAL_CORE_VERSION = '0.0.0';

export type HostId = 'paradex' | 'paradigm' | 'standalone';

// --- Types ---
export type {
  AgentConnectionStatus,
  ApprovalRequest,
  ChatAttachment,
  ChatMessage,
  ChatSession,
  CustomFolder,
  GatewayEvent,
  GatewayFrame,
  GatewayRequest,
  GatewayResponse,
  MessageAttachment,
  ModelInfo,
  ModelSupports,
  SessionFolder,
  ToolCall,
  UiRender,
} from './types.js';

// --- Context-window usage ---
export type { SessionContextUsage } from './session-usage.js';
export {
  parseSessionContextUsage,
  reconcileSessionContextUsage,
} from './session-usage.js';

// --- Sub-agent subtasks ---
export type {
  RunningSubtask,
  RunningSubtasks,
  SubagentActivity,
  SubtaskUpdate,
} from './subagent-tasks.js';
export {
  applySubagentActivity,
  clearSubtasksFor,
  countRunningSubtasks,
  MAX_TRACKED_SUBTASKS,
  NO_RUNNING_SUBTASKS,
  parseSubagentActivity,
  pruneStaleSubtasks,
  SUBTASK_STALE_MS,
} from './subagent-tasks.js';

// --- Credential envelope (browser-side sealing of venue API credentials) ---
export type {
  CredentialEnvelope,
  SealCredentialInput,
} from './credential-envelope.js';
export {
  CREDENTIAL_ENVELOPE_ALG,
  CREDENTIAL_ENVELOPE_VERSION,
  credentialAad,
  sealCredential,
} from './credential-envelope.js';

// --- Device identity ---
export type {
  DeviceHandshake,
  DeviceHandshakeInput,
  DeviceIdentity,
  DeviceIdentityStore,
  DeviceIdentityStoreConfig,
  StoredCryptoKeyDevice,
} from './device-identity.js';
export { createDeviceIdentityStore } from './device-identity.js';

export type {
  LegacyDeviceKeyMigrator,
  LegacyDeviceKeyMigratorConfig,
} from './device-identity-migration.js';
export { createLegacyDeviceKeyMigrator } from './device-identity-migration.js';

// --- Agent URL + token storage ---
export type {
  AgentUrlScope,
  AgentUrlStore,
  AgentUrlStoreConfig,
  StorageLike,
} from './agent-url.js';
export {
  createAgentUrlStore,
  createMemoryStorage,
  deriveAgentDashboardUrl,
  isSafeAppUrl,
  isValidAgentUrl,
} from './agent-url.js';

// --- Chat history storage ---
export type {
  ChatHistoryScope,
  ChatHistoryStore,
  ChatHistoryStoreConfig,
  DeletionTombstone,
  HistoryUiState,
  PendingCleanup,
} from './chat-history-storage.js';
export {
  createChatHistoryStore,
  deriveSessionTitleFromMessages,
  generateChatSessionId,
  generateMessageId,
  generateSessionId,
  PENDING_CLEANUP_MAX_AGE_MS,
  PERSISTED_HISTORY_HARD_CAP,
  PERSISTED_THINKING_BYTE_BUDGET,
  PERSISTED_THUMBNAIL_BYTE_BUDGET,
  clearLiveMessageFlags,
  isPersistedChatMessage,
  sanitizeMessageAttachments,
  sanitizeMessageThinking,
  sanitizeMessageToolCalls,
  stripThinkingForSync,
  TOOL_CALLS_WINDOW,
  TOOL_NAME_MAX_CHARS,
  trimMessagesForPersistence,
  SESSION_TITLE_MAX_LEN,
} from './chat-history-storage.js';

// --- User preferences (settings; synced via the `ui-state` doc, except
// `DEVICE_LOCAL_PREFERENCE_KEYS`) ---
export type {
  FontPreference,
  LanguagePreference,
  PreferenceKey,
  PreferencesDoc,
  PreferencesStore,
  PreferencesStoreConfig,
  TelemetryPreference,
  TerminalPreferences,
  ThemePreference,
  UiScale,
} from './preferences.js';
export {
  allLanguageDirectives,
  createPreferencesStore,
  DEFAULT_PREFERENCES,
  DEVICE_LOCAL_PREFERENCE_KEYS,
  EMPTY_PREFERENCES_DOC,
  FONT_STACKS,
  GATEWAY_DEFAULT_SENTINEL,
  LANGUAGES,
  languageDirective,
  mergePreferences,
  hasClampedStamp,
  isDeviceLocalPreference,
  normalizePreferencesDoc,
  preferencesDocsEqual,
  setPreference,
  toSyncedPreferencesDoc,
  UI_SCALES,
} from './preferences.js';

// --- Gateway session keys (openclaw's `dashboard:` title scope) ---
export {
  GATEWAY_TITLE_SCOPE,
  isScopedChatId,
  SCOPED_CHAT_ID_MARKER,
  stripTitleScope,
  toGatewaySessionKey,
} from './session-keys.js';

// --- Gateway session organisation (`sessions.*` over the socket) ---
export type {
  GatewayRequester,
  GatewaySessionGroup,
  GatewaySessionRow,
  GatewaySessionsListResult,
  GatewaySessionsSubscribeResult,
  PatchedSessionModel,
  ProjectSessionsOptions,
  SessionOrganisationPatch,
} from './gateway-sessions.js';
export {
  adoptableRowTitle,
  bareSessionId,
  deleteGatewaySession,
  deleteSessionGroup,
  foldersFromGatewayGroups,
  isSlashCommandText,
  isUntitledRow,
  isTerminalChatRow,
  projectGatewaySessions,
  listSessionGroups,
  listSessions,
  patchSessionModel,
  patchSessionOrganisation,
  putSessionGroups,
  renameSessionGroup,
  resetSession,
  sessionRowTitle,
  sessionRowUpdatedAt,
  subscribeSessions,
} from './gateway-sessions.js';

// --- Gateway transcript reads (`chat.history` → bubbles) ---
export type {
  GatewayHistoryFraming,
  GatewayHistoryPage,
  GatewayHistoryRow,
} from './gateway-history.js';
export {
  pageTranscriptId,
  projectGatewayHistory,
  readGatewayRowContent,
  sendKeyFromRow,
  stripOutboundFraming,
} from './gateway-history.js';

// --- Files the AGENT produced, named on a live frame as pod paths ---
export {
  attachmentFromMediaPath,
  attachmentsFromMediaPaths,
} from './media-attachments.js';

// --- Session sync (native, gateway-backed cross-device history) ---
export type {
  RemoteHydrationResult,
  TerminalIndexDoc,
  TerminalMessagesDoc,
  TerminalSync,
  TerminalUiStateDoc,
} from './session-sync.js';
export {
  buildIndexDoc,
  createSessionSync,
  fetchAndMergeSessionFromRemote,
  hydrateFromRemote,
} from './session-sync.js';
export type {
  SyncDocRef,
  SyncEnvelope,
  SyncSnapshot,
  TerminalSyncClientConfig,
  TerminalSyncStatus,
} from './sync/index.js';
export {
  emptyIndexDoc,
  mergeIndex,
  mergeMessages,
  syncBaseUrlFromAgentUrl,
  TerminalSyncClient,
} from './sync/index.js';

// --- OpenClaw WebSocket client ---
export type {
  AgentDeviceSigner,
  AgentTokenProvider,
  AgentWebSocket,
  GatewayServerInfo,
  OpenClawClientConfig,
} from './createAgentWebSocket.js';
export {
  buildClientId,
  createOpenClawClient,
  GatewayRequestError,
  defaultClientIdSuffix,
  DEFAULT_OPERATOR_SCOPES,
  estimateChatSendBytes,
  MAX_CHAT_SEND_BYTES,
  parseGatewayServerInfo,
  parseProtocolMismatch,
} from './createAgentWebSocket.js';
